import React, { useState } from "react";
import { Loader2, CreditCard, Lock } from "lucide-react";
import { useInitiateAssessmentFeeMutation } from "../api/logbookApi";
import { submitOnePayForm } from "../utils/onepay";

/** Shown in place of the pay button once the secretariat closes the window. */
export const ASSESSMENT_PAYMENT_CLOSED_MESSAGE =
  "Payment is closed, assessment is ongoing";

/**
 * Whether a logbook may still be paid for. Driven by the API's
 * `assessment_payment_open` flag (LOGBOOK_ASSESSMENT_PAYMENT_OPEN in the API
 * .env). Anything other than an explicit `true` counts as closed, so an API
 * that has not been redeployed with the flag keeps payment shut.
 */
export function isAssessmentPaymentOpen(logbook: any): boolean {
  return logbook?.assessment_payment_open === true;
}

/** Static "payment closed" pill — same footprint as the pay button. */
export function AssessmentPaymentClosedNotice({
  className = "",
}: {
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 border border-gray-200 text-gray-500 text-[11px] font-semibold leading-tight text-center ${className}`}
    >
      <Lock size={13} className="shrink-0" /> {ASSESSMENT_PAYMENT_CLOSED_MESSAGE}
    </span>
  );
}

/**
 * Animated "Make payment for assessment" button. Initiates the assessment-fee
 * OnePay payment for a submitted logbook and redirects to the gateway — the same
 * flow as the logbook detail page. The glow around it draws attention to the CTA.
 *
 * When `paymentOpen` is not true the button is replaced by the closed notice.
 */
export default function PayAssessmentButton({
  logbookId,
  label = "Make payment for assessment",
  className = "",
  paymentOpen,
  onError,
}: {
  logbookId: string;
  label?: string;
  className?: string;
  paymentOpen?: boolean;
  onError?: (msg: string) => void;
}) {
  const [initiate, { isLoading }] = useInitiateAssessmentFeeMutation();
  const [err, setErr] = useState<string | null>(null);

  // Payment window closed — no CTA, just the notice.
  if (paymentOpen !== true) {
    return <AssessmentPaymentClosedNotice className={className} />;
  }

  const onPay = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setErr(null);
    try {
      const res: any = await initiate({ logbookId }).unwrap();
      if (!res?.merchant_reg_id || !res?.merch_trans_id) {
        const m = "Invalid payment response. Please try again.";
        setErr(m);
        onError?.(m);
        return;
      }
      submitOnePayForm(res);
    } catch (e: any) {
      const m = e?.data?.message || "Could not start payment. Please try again.";
      setErr(m);
      onError?.(m);
    }
  };

  return (
    <span className="relative inline-flex">
      {/* Pulsing glow — the "animation around the button". */}
      {!isLoading && (
        <span
          aria-hidden
          className="absolute -inset-1 rounded-xl bg-amber-400/60 blur-sm animate-pulse pointer-events-none"
        />
      )}
      <button
        type="button"
        onClick={onPay}
        disabled={isLoading}
        title={err || undefined}
        className={`relative inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 transition ${className}`}
      >
        {isLoading ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Redirecting…
          </>
        ) : (
          <>
            <CreditCard size={14} /> {label}
          </>
        )}
      </button>
    </span>
  );
}
